import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type { PublicApiKey, PublicApiScope } from '../api/client';
import { Btn, Card, ErrorLine, Loading, Row, Section, Select, TextInput, Toggle, XButton } from './ChatAssistant/compact/kit';
import { C } from './ChatAssistant/compact/tokens';
import { font as f } from './ChatAssistant/compact/style';

const DOCS_URL = 'https://api.onlymindai.com/v1/docs';

type Expiry = 'never' | '30' | '90' | '365';

const messageOf = (err: unknown) => (err instanceof Error ? err.message : '');

/**
 * Settings → Account → API keys: the person's keys for the Public API (ADR 0041).
 * A key acts as its User and only reads what that User may see; it is shown once,
 * when created. Distinct from the Console key below it.
 */
export function PublicApiKeys() {
  const { t } = useTranslation('account');
  const [keys, setKeys] = useState<PublicApiKey[] | null>(null);
  const [scopes, setScopes] = useState<PublicApiScope[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [expiry, setExpiry] = useState<Expiry>('never');
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [k, s] = await Promise.all([api.listPublicApiKeys(), api.listPublicApiScopes()]);
      setKeys(k);
      setScopes(s);
      setError(null);
    } catch (err) {
      setError(messageOf(err) || t('publicApiKeys.loadFailed'));
      setKeys([]);
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    setName('');
    setExpiry('never');
    setChosen(scopes.filter((s) => s.available).map((s) => s.scope));
    setCreating(true);
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const expires_at = expiry === 'never' ? null : new Date(Date.now() + Number(expiry) * 86_400_000).toISOString();
      const created = await api.createPublicApiKey({ name: name.trim(), scopes: chosen, expires_at });
      setRevealed(created.key);
      setCreating(false);
      await load();
    } catch (err) {
      setError(messageOf(err) || t('publicApiKeys.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.revokePublicApiKey(id);
      setConfirmRevoke(null);
      await load();
    } catch (err) {
      setError(messageOf(err) || t('publicApiKeys.revokeFailed'));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('publicApiKeys.copyFailed'));
    }
  };

  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : t('apiKey.never'));
  const scopeLabel = (s: string) => t(`publicApiKeys.scopes.${s}`, { defaultValue: s });
  const status = (k: PublicApiKey) =>
    k.revoked_at ? t('publicApiKeys.revokedOn', { date: when(k.revoked_at) })
      : !k.active ? t('publicApiKeys.expiredOn', { date: when(k.expires_at) })
        : k.expires_at ? t('publicApiKeys.expiresOn', { date: when(k.expires_at) }) : null;

  const mono = { font: f(500, 12.5), fontFamily: 'var(--font-mono)', color: C.text } as const;
  const anyScope = scopes.some((s) => s.available);
  const canCreate = name.trim().length > 0 && chosen.length > 0 && !busy;

  return (
    <Section
      title={t('publicApiKeys.title')}
      hint={<>{t('publicApiKeys.description')} <a href={DOCS_URL} target="_blank" rel="noreferrer" style={{ color: C.purplePale }}>{t('publicApiKeys.docs')}</a></>}
      right={keys && !creating && !revealed && anyScope ? <Btn kind="primary" icon="plus" onClick={openCreate}>{t('publicApiKeys.newKey')}</Btn> : undefined}
    >
      {error && (
        <ErrorLine>
          <span style={{ flex: 1 }}>{error}</span>
          <XButton onClick={() => setError(null)} />
        </ErrorLine>
      )}

      {revealed && (
        <Card>
          <Row stack label={t('apiKey.key')} help={t('apiKey.storeSecurely')} helpColor={C.purplePale}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <code style={{ ...mono, flex: '1 1 260px', minWidth: 0, padding: '6px 8px', borderRadius: 6, background: C.input, border: `1px solid ${C.border}`, wordBreak: 'break-all' }}>{revealed}</code>
              <Btn kind={copied ? 'tint' : 'ghost'} icon={copied ? 'check' : 'copy'} onClick={() => void copy()}>
                {copied ? t('apiKey.copied') : t('apiKey.copy')}
              </Btn>
            </div>
          </Row>
          <div style={{ padding: '0 14px 12px' }}>
            <Btn kind="primary" onClick={() => { setRevealed(null); setCopied(false); }}>{t('apiKey.savedMyKey')}</Btn>
          </div>
        </Card>
      )}

      {creating && (
        <Card clip={false}>
          <Row label={t('publicApiKeys.name')} help={t('publicApiKeys.nameHint')}>
            <TextInput value={name} onChange={setName} placeholder={t('publicApiKeys.namePlaceholder')} width={220} />
          </Row>
          {scopes.map((s) => (
            <Row key={s.scope} label={scopeLabel(s.scope)} help={s.available ? t(`publicApiKeys.scopeHints.${s.scope}`, { defaultValue: '' }) : t(`publicApiKeys.scopeUnavailableBy.${s.scope}`, { defaultValue: t('publicApiKeys.scopeUnavailable') })}>
              <Toggle
                on={chosen.includes(s.scope)}
                disabled={!s.available}
                onChange={(on) => setChosen((c) => (on ? [...c, s.scope] : c.filter((x) => x !== s.scope)))}
              />
            </Row>
          ))}
          <Row label={t('publicApiKeys.expires')}>
            <Select<Expiry>
              value={expiry}
              onChange={setExpiry}
              width={160}
              options={[
                { id: 'never', label: t('publicApiKeys.expiry.never') },
                { id: '30', label: t('publicApiKeys.expiry.days', { count: 30 }) },
                { id: '90', label: t('publicApiKeys.expiry.days', { count: 90 }) },
                { id: '365', label: t('publicApiKeys.expiry.days', { count: 365 }) },
              ]}
            />
          </Row>
          <div style={{ display: 'flex', gap: 8, padding: '6px 14px 12px' }}>
            <Btn kind="primary" icon="key" onClick={() => void create()} disabled={!canCreate}>
              {busy ? t('apiKey.processing') : t('publicApiKeys.create')}
            </Btn>
            <Btn kind="muted" onClick={() => setCreating(false)} disabled={busy}>{t('apiKey.cancel')}</Btn>
          </div>
        </Card>
      )}

      {keys === null ? (
        <Card><div style={{ padding: '0 14px' }}><Loading /></div></Card>
      ) : keys.length === 0 ? (
        !creating && (
          <Card>
            <Row label={t('publicApiKeys.none')} help={anyScope ? t('publicApiKeys.noneHint') : t('publicApiKeys.noStatsAccess')} />
          </Card>
        )
      ) : (
        <Card>
          {keys.map((k) => (
            <div key={k.id} style={{ opacity: k.active ? 1 : 0.55 }}>
              {confirmRevoke === k.id ? (
                <Row stack label={t('publicApiKeys.revokeTitle', { name: k.name })} help={t('publicApiKeys.revokeMessage')}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn kind="primary" onClick={() => void revoke(k.id)} disabled={busy}>{busy ? t('apiKey.processing') : t('apiKey.revoke')}</Btn>
                    <Btn kind="muted" onClick={() => setConfirmRevoke(null)} disabled={busy}>{t('apiKey.cancel')}</Btn>
                  </div>
                </Row>
              ) : (
                <Row
                  label={<>{k.name} <code style={{ ...mono, color: C.sub, marginLeft: 6 }}>{k.key_prefix}••••</code></>}
                  help={[
                    k.scopes.map(scopeLabel).join(', '),
                    t('publicApiKeys.createdOn', { date: when(k.created_at) }),
                    t('publicApiKeys.lastUsedOn', { date: when(k.last_used_at) }),
                    status(k),
                  ].filter(Boolean).join(' · ')}
                >
                  {k.active && <Btn kind="muted" icon="slash" onClick={() => setConfirmRevoke(k.id)} disabled={busy}>{t('apiKey.revoke')}</Btn>}
                </Row>
              )}
            </div>
          ))}
        </Card>
      )}
    </Section>
  );
}
